import SEND_MESSAGE_MUTATION from './mutation.sendMessage.gql'
import { useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { TextareaField } from '@careguide/forms'
import Button from 'components/generic/Button/Button'
import { useMutation } from '@apollo/client'
import Stack from '@careguide/stack'
import { SendMessage, SendMessageVariables } from 'graph/sendMessage'

type MessageSenderProps = {
  listingId: string
  recipient: {
    firstName: string
  }
  defaultMessage: string
  onSend: (successful: boolean) => void
  onError?: (error: string) => void
  checkCanSendMessage(): Promise<boolean>
}

type MessageFormData = {
  message: string
}

const MessageSender = ({
  listingId,
  recipient,
  defaultMessage,
  onSend,
  onError,
  checkCanSendMessage,
}: MessageSenderProps) => {
  const { firstName } = recipient
  const [sendMessageMutation, { data, error: mutationError }] =
    useMutation<SendMessage, SendMessageVariables>(SEND_MESSAGE_MUTATION)

  const { register, handleSubmit, errors } = useForm<MessageFormData>({
    defaultValues: {
      message: defaultMessage,
    },
  })

  const validationRules = {
    required: `Looks like you've yet to write something. Write ${firstName} a message to let them know you're interested.`,
  }

  const onSubmit = async ({ message }: MessageFormData) => {
    const variables = {
      input: {
        message,
        listingId,
        clientMutationId: '0',
      },
    }
    try {
      if (await checkCanSendMessage()) {
        await sendMessageMutation({ variables })
      }
    } catch (err) {
      return // mutationError is sufficient
    }
  }

  useEffect(() => {
    if (data) {
      if (data.sendMessage?.message?.filteredForCurrentUser === true) {
        onSend(false)
      } else {
        onSend(true)
      }
    }

    if (typeof onError === 'function') {
      onError(mutationError?.message || '')
    }
  }, [data, mutationError])

  if (data) {
    return null
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Stack gap='3'>
        <TextareaField
          name='message'
          rows={4}
          autoFocus={true}
          error={errors.message?.message || mutationError?.message}
          ref={register(validationRules)}
        />
        <Button fluid>Send A Message</Button>
      </Stack>
    </form>
  )
}

export default MessageSender
